import { Navigate } from 'react-router-dom'
import { useIsAuthenticated } from '@azure/msal-react'
import { useAuth } from '@/providers/AuthProvider'
import { hasAnyGrant } from '@/auth/permissions'
import { Loader2 } from 'lucide-react'
import { ReactNode } from 'react'

interface AdminRouteProps {
  children: ReactNode
}

/**
 * Route guard component that restricts access to admin-only pages.
 * Redirects non-admin users to the employee journey page.
 */
export function AdminRoute({ children }: AdminRouteProps) {
  const { user, arePermissionsLoading } = useAuth()
  const isAuthenticated = useIsAuthenticated()

  // On hard refresh MSAL rehydrates the account synchronously, but the
  // /auth/me fetch that populates roles is async. Hold off on the role
  // check until the user profile has loaded — otherwise we'd redirect
  // admins away to /employee-journey before their roles arrive.
  // Same reasoning as the roles wait below: a user whose access comes from a
  // permission GRANT looks unauthorised until those grants arrive, so deciding
  // now would bounce them off the very page their grant unlocks.
  if (isAuthenticated && user !== null && arePermissionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh] text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        <span className="text-sm">Loading…</span>
      </div>
    )
  }

  if (isAuthenticated && user === null) {
    return (
      <div className="flex items-center justify-center min-h-[50vh] text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        <span className="text-sm">Loading…</span>
      </div>
    )
  }

  const hasAdminRole = user?.roles?.some(role =>
    role === 'admin' || role === 'hr_admin' || role === 'hrbp' || role === 'fno_admin' || role === 'data_team'
  )

  // Someone granted a single capability (e.g. a Head of Finance with budget:read)
  // holds none of these roles, but must still reach the page their grant lives on.
  // The page itself then shows only what they're entitled to, and every request
  // is authorized server-side regardless.
  if (!hasAdminRole && !hasAnyGrant(user)) {
    return <Navigate to="/employee-journey" replace />
  }

  return <>{children}</>
}
